import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
// @ts-ignore
import Swal from 'sweetalert2';

import { useCreateRewardMutation, useUpdateRewardMutation } from '../../rewards.service';
import { Reward, RewardProvider } from '../../types/reward.entity';
import { getRewardValidationSchema, CreateRewardFormData } from '../../utils/reward.validation';
import { uploadFilesToFirebase } from 'src/firebase';
import { InputFile } from 'src/components/Form/upload-input';

import { RewardFormHeader } from './RewardFormHeader';
import { RewardBasicInfoFields } from './RewardBasicInfoFields';
import { RewardProviderFields } from './RewardProviderFields';
import { RewardFormActions } from './RewardFormActions';
import { RewardProviderSummary } from './RewardProviderSummary';
import { RewardMediaFields } from './RewardMediaFields';
import {
  buildTremendousAttachment,
  buildTremendousProductMetadata,
  extractRewardBrandName,
  isTremendousRewardProductMetadata,
} from '../../utils/tremendous';

interface RewardFormProps {
  initialData?: Reward;
}

export const RewardForm: React.FC<RewardFormProps> = ({ initialData }) => {
  const navigate = useNavigate();
  const [createReward, { isLoading: isCreating }] = useCreateRewardMutation();
  const [updateReward, { isLoading: isUpdating }] = useUpdateRewardMutation();
  const isEditMode = !!initialData;
  const isLoading = isCreating || isUpdating;

  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const formatTimestampToLocal = (ts?: any): string => {
    if (!ts) return '';
    try {
      const date = ts.toDate ? ts.toDate() : new Date(ts);
      return date.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const initialProductMetadata = initialData?.provider?.productMetadata as Record<string, unknown> | undefined;
  const initialTremendousMetadata = isTremendousRewardProductMetadata(initialProductMetadata)
    ? initialProductMetadata
    : null;

  const formSchema = getRewardValidationSchema(isEditMode, !!initialData?.attachment);

  const methods = useForm<CreateRewardFormData>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      amount: initialData?.amount || 100,
      category: (initialData?.category as any) || 'DIGITAL',
      isAvailable: initialData?.isAvailable ?? true,
      providerName: (initialData?.provider?.name || 'TREMENDOUS') as RewardProvider,
      amazonCurrency: (initialData?.provider?.productMetadata?.currency as string) || 'USD',
      amazonAmount: (initialData?.provider?.productMetadata?.amount as number) || 10,
      tremendousProduct: initialTremendousMetadata ? {
        id: initialTremendousMetadata.productId,
        name: initialTremendousMetadata.productNameSnapshot,
        description: initialTremendousMetadata.descriptionSnapshot,
        category: initialTremendousMetadata.category,
        subcategory: initialTremendousMetadata.subcategory,
        disclosure: initialTremendousMetadata.disclosure,
        countries: initialTremendousMetadata.countries,
        skus: [
          {
            min: initialTremendousMetadata.skuMin,
            max: initialTremendousMetadata.skuMax,
            currencyCodes: [initialTremendousMetadata.currencyCode],
          },
        ],
        imageUrl: initialTremendousMetadata.imageUrl,
      } : undefined,
      tremendousCurrencyCode: initialTremendousMetadata?.currencyCode || '',
      tremendousDenomination: initialTremendousMetadata?.denomination,
      countryList: initialData?.availableCountries || [],
      expiresAt: formatTimestampToLocal(initialData?.expiresAt),
      mediaFiles: [],
    }
  });

  const onSubmit = async (data: CreateRewardFormData) => {
    try {
      let productMetadata: Record<string, unknown> = {};
      
      if (data.providerName === 'AMAZON') {
        productMetadata = {
          currency: data.amazonCurrency,
          amount: data.amazonAmount
        };
      }

      let attachmentPayload = initialData?.attachment || null;

      if (data.providerName === 'TREMENDOUS') {
        if (!data.tremendousProduct || !data.tremendousCurrencyCode || !data.tremendousDenomination) {
          throw new Error('Select a Tremendous product, currency, and denomination before saving.');
        }

        const tremendousMetadata = buildTremendousProductMetadata(
          data.tremendousProduct,
          data.tremendousCurrencyCode,
          data.tremendousDenomination
        );

        if (!tremendousMetadata) {
          throw new Error('The selected Tremendous currency is not supported by this product.');
        }

        productMetadata = tremendousMetadata;
        attachmentPayload = buildTremendousAttachment(data.tremendousProduct) || attachmentPayload;
      }

      if (data.mediaFiles && data.mediaFiles.length > 0) {
        const generatedPathId = Math.random().toString(36).substr(2, 9);
        const attachments = await uploadFilesToFirebase(data.mediaFiles as InputFile[], `rewards/${generatedPathId}`);
        const uploadedFile = attachments.length > 0 ? attachments[0] : null;
        if (uploadedFile) {
          attachmentPayload = {
            ...uploadedFile,
          } as any;
        }
      }

      const brandSourceName =
        data.providerName === 'TREMENDOUS' && data.tremendousProduct
          ? data.tremendousProduct.name
          : data.name.trim();
      const brandName = extractRewardBrandName(brandSourceName);

      const payload = {
        ...(isEditMode && { id: initialData!.id }),
        name: data.name.trim(),
        brandName,
        description: data.description.trim(),
        amount: data.amount,
        category: data.category,
        isAvailable: data.isAvailable,
        provider: {
          name: data.providerName,
          ...(Object.keys(productMetadata).length > 0 && { productMetadata })
        },
        ...(attachmentPayload ? { attachment: attachmentPayload } : {}),
        availableCountries: data.countryList,
        ...(data.expiresAt ? { expiresAt: new Date(data.expiresAt).toUTCString() } : {}),
        metadata: {
          ...(isEditMode ? (initialData?.metadata || {}) : {}),
          brandName,
        }
      };

      if (isEditMode) {
        await updateReward({...payload, rewardId: initialData!.id}).unwrap();
      } else {
        await createReward(payload).unwrap();
      }

      Swal.fire({
        title: 'Success! 🎉',
        text: isEditMode ? 'Reward has been updated successfully.' : 'Reward has been created successfully.',
        icon: 'success',
        confirmButtonColor: '#4f46e5',
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' }
      }).then(() => {
        navigate(isEditMode ? `/rewards/${initialData!.id}` : '/rewards');
      });
      
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} reward:`, error);
      Swal.fire({
        title: 'Error! ❌',
        text: error?.message || `Failed to ${isEditMode ? 'update' : 'create'} reward. Please try again.`,
        icon: 'error',
        confirmButtonColor: '#ef4444',
        showClass: { popup: 'animate__animated animate__fadeInDown' }
      });
    }
  };

  const onInvalidSubmit = () => {
    Swal.fire({
      title: "Missing Required Fields",
      text: "Please complete all required fields before creating this reward.",
      icon: "warning",
      confirmButtonColor: "#f59e0b",
      showClass: { popup: "animate__animated animate__fadeInDown" },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <RewardFormHeader isEditMode={isEditMode} animateIn={animateIn} />

        <FormProvider {...methods}>
          <form 
            onSubmit={methods.handleSubmit(onSubmit, onInvalidSubmit)} 
            className={`space-y-8 transition-all duration-700 delay-100 ease-out transform ${animateIn ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
          >
            <div className="space-y-8">
              {!isEditMode ? (
                <RewardProviderFields />
              ) : (
                <RewardProviderSummary reward={initialData!} />
              )}

              <RewardBasicInfoFields />

              <RewardMediaFields
                isEditMode={isEditMode}
                initialDataAttachmentUrl={initialData?.attachment?.fileUrl}
              />
            </div>
            
            <RewardFormActions isLoading={isLoading} isEditMode={isEditMode} />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default RewardForm;
